import { useState, useEffect } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { Card } from "../app/components/primitives/card";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Skeleton } from "../app/components/primitives/skeleton";
import { ErrorPanel } from "../app/components/patterns/ErrorPanel";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import type { ForecastScenario } from "@shared-core/data/adapter";

export function SettingsScenariosPage() {
  const adapter = useDataAdapter();
  const [scenarios, setScenarios] = useState<ForecastScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editedMultipliers, setEditedMultipliers] = useState<
    Record<string, number>
  >({});

  async function loadScenarios() {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await adapter.getScenarios();
      setScenarios(data);
      const initial: Record<string, number> = {};
      for (const s of data) {
        initial[s.id] = s.outputMultiplier;
      }
      setEditedMultipliers(initial);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadScenarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch; loadScenarios is recreated every render
  }, []);

  function handleMultiplierChange(scenarioId: string, value: string) {
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      setEditedMultipliers((prev) => ({ ...prev, [scenarioId]: parsed }));
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const updates = scenarios.filter(
        (s) => editedMultipliers[s.id] !== s.outputMultiplier,
      );
      await Promise.all(
        updates.map((s) =>
          adapter.updateScenario(s.id, editedMultipliers[s.id]),
        ),
      );
      toast.success("Scenarios updated");
      await loadScenarios();
    } catch (error) {
      toast.error("Failed to save scenarios");
      console.error("Failed to save scenarios:", error);
    } finally {
      setSaving(false);
    }
  }

  const hasChanges = scenarios.some(
    (s) => editedMultipliers[s.id] !== s.outputMultiplier,
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Forecast Scenarios</h1>
          <p className="text-muted-foreground mt-1">
            Configure output multipliers for scenario analysis
          </p>
        </div>
        <Card className="p-6 space-y-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </Card>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Forecast Scenarios</h1>
          <p className="text-muted-foreground mt-1">
            Configure output multipliers for scenario analysis
          </p>
        </div>
        <ErrorPanel message={loadError} onRetry={loadScenarios} />
      </div>
    );
  }

  const likely = scenarios.find((s) => s.name.toLowerCase().includes("likely"));
  const good = scenarios.find((s) => s.name.toLowerCase().includes("good"));
  const bad = scenarios.find((s) => s.name.toLowerCase().includes("bad"));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Forecast Scenarios</h1>
        <p className="text-muted-foreground mt-1">
          Configure output multipliers for scenario analysis
        </p>
      </div>

      <Card className="p-6 space-y-6">
        {likely && (
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{likely.name}</div>
              <div className="text-sm text-muted-foreground">
                Base scenario (read-only)
              </div>
            </div>
            <Input
              type="number"
              value={likely.outputMultiplier}
              disabled
              className="w-32 text-right"
            />
          </div>
        )}

        {good && (
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{good.name}</div>
              <div className="text-sm text-muted-foreground">
                Optimistic multiplier
              </div>
            </div>
            <Input
              type="number"
              step={0.01}
              value={editedMultipliers[good.id] ?? good.outputMultiplier}
              onChange={(e) => handleMultiplierChange(good.id, e.target.value)}
              className="w-32 text-right"
            />
          </div>
        )}

        {bad && (
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{bad.name}</div>
              <div className="text-sm text-muted-foreground">
                Pessimistic multiplier
              </div>
            </div>
            <Input
              type="number"
              step={0.01}
              value={editedMultipliers[bad.id] ?? bad.outputMultiplier}
              onChange={(e) => handleMultiplierChange(bad.id, e.target.value)}
              className="w-32 text-right"
            />
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save changes"
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}
